/**
 * 生产二次确认（G3）—— 一次性 `confirmToken` 的唯一收口 —— 方案 §5.3 / §6.4
 *
 * 后端在**生产环境**对 5 个写操作（`batch/create`、`batch/stop`、`batch/republish`、
 * `service/stop`、`service/retry`）一律校验一次性 `confirmToken`：`verifyProdConfirm` 拿它去
 * Redis 查「用途:目标对象」绑定关系，查不到就是 `07020041`。
 * 所以前端必须先调 `POST /lpaasPublish/confirm/issue` 换 token，把返回值原样附在写请求上。
 *
 * 🔴 历史事故（本 hook 就是来钉死它的）：前端曾经硬编码一个 `'CONFIRMED'` 占位串提交，
 *    后端 Redis 里没有它 → **生产（master）的发布/停止/重发全量不可用**，而 sit 一切正常，
 *    所以人肉联调测不出来。自此「token 只能来自 /confirm/issue」写进契约。
 *
 * 三条设计取舍：
 *  1. **前端不自己判「这批是不是生产」**（除非调用方明确知道）：分支→环境的映射在 Apollo
 *     （branchStrategies），前端拿 `branch === 'master'` 硬判会与后端不一致。详情页 4 个写操作
 *     的做法是「先按不带 token 发，撞 07020041 再问后端」——`/confirm/issue` 会回 `prodEnv`，
 *     它就是后端判的答案（`prodEnv=false` 说明不是「缺确认」而是 G2 环境不一致，不该弹确认框）。
 *     ⚠️ 唯一的例外是**新建发布**：`/batch/create` 此刻还没有批次对象，后端 `prodEnv` 只能保守
 *     返「生产开没开」而与真实环境无关，所以新建发布仍由调用方按分支传 `prodbranch`。
 *  2. **同一把 requestId 贯穿重试**：一次用户操作 = 一把幂等键（方案 §5.1），
 *     确认后重试必须复用，不能换新 ID（换了就变成「第二次提交」）。
 *  3. **sit 一次都不调 `/confirm/issue`**：非生产走不到 07020041，也就不会弹确认框 ——
 *     否则用户会被「见框就点确定」训练出盲点习惯，二次确认就白做了（§6.4 差异 #10）。
 *
 * 依赖方向：hook 可以 import services（与 useLpaasAuth 一致）；本文件不渲染任何东西，
 * 确认框由调用方拿 `visible/title/note` 去渲染 `ProdConfirmModal`。
 */
import { useRef, useState } from 'react';
import { TIP, TEXT, isSuccessCode } from '../constants';
import { isProdConfirmRequired, toUiAction } from '../utils';
import { issueConfirmToken } from '@/services/lpaasPublish';

/**
 * @param {Object}   opts
 * @param {string}   opts.purpose      constants.CONFIRM_PURPOSE.*（🔴 与后端枚举逐字一致）
 * @param {number}   [opts.targetId]   批次/服务 ID；新建批次没有对象，传 null
 * @param {string}   opts.requestId    本次用户操作的幂等键（hook 只透传，不生成、不更换）
 * @param {string}   [opts.title]      确认框标题（详情页按动作区分，新建发布用默认那句）
 * @param {string}   [opts.message]    确认框里那句「上述影响」的具体内容（选填）
 * @param {boolean}  [opts.prodbranch] 调用方是否已确定目标在生产：
 *                                     true = 先弹确认框再发；false = 明确不是生产，永不弹框；
 *                                     不传 = 不确定，撞 07020041 时问后端 `prodEnv`
 * @param {Function} opts.call         (confirmToken, requestId) => Promise<写操作回包>
 * @param {boolean}  [opts.confirmFirst] true = 发请求之前就弹确认框（新建发布选 master 的路径）
 * @param {Function} [opts.onSuccess]  (res) => void 最终成功（唯一允许做「成功态 UI」的地方）
 * @param {Function} [opts.onError]    (action) => void 最终失败，action 是 toUiAction 的动作对象
 */
export default function useProdConfirm() {
  const [view, setView] = useState({
    visible: false,
    loading: false,
    title: '',
    message: '',
    note: '',
  });
  /** 当前这一次用户操作的上下文（run 写入、成功/最终失败后清空） */
  const task = useRef(null);
  /** 已经取到手、正等用户勾选确认的一次性 token（用户点确认时直接用，不重复签发） */
  const held = useRef(null);

  const clearTask = () => {
    task.current = null;
    held.current = null;
  };
  const hide = (extra) =>
    setView((v) => ({
      ...v,
      visible: false,
      loading: false,
      note: '',
      ...extra,
    }));

  /** 终态失败：清上下文 + 关框 + 交调用方提示（🔴 一个字节都不许吞） */
  const fail = (action) => {
    const t = task.current;
    clearTask();
    hide();
    if (t && t.onError) t.onError(action);
  };

  /** 取一次性 token。失败原样回 { ok:false, action }，由调用方决定提示方式 */
  const issue = async (t) => {
    let res;
    try {
      res = await issueConfirmToken({
        purpose: t.purpose,
        targetId: t.targetId ?? null,
      });
    } catch (e) {
      // 服务层已把网络异常包成信封，这里只是兜底：任何异常都不许往上抛（弹窗会白屏）
      return {
        ok: false,
        action: { type: 'toast', msg: TEXT.CONFIRM_TOKEN_EMPTY },
      };
    }
    const action = toUiAction(res);
    if (action) return { ok: false, action };
    const confirmToken = res.data && res.data.confirmToken;
    if (!confirmToken) {
      // 成功码却没给 token：宁可拦下，也不拿空串去提交（否则必然 07020041，还查不出原因）
      return {
        ok: false,
        action: { type: 'toast', msg: TEXT.CONFIRM_TOKEN_EMPTY },
      };
    }
    return {
      ok: true,
      confirmToken,
      prodEnv: !!(res.data && res.data.prodEnv),
    };
  };

  /**
   * 发一次写请求。`token=null` 即「还没确认过」的那一次。
   * @return {Promise<Object|null>} 写操作回包（失败也原样返回，分流已在这里做完）
   */
  const attempt = async (token) => {
    const t = task.current;
    if (!t) return null;
    let res;
    try {
      res = await t.call(token || null, t.requestId);
    } catch (e) {
      // 🔴 异常不许往上抛（调用方全是 async 事件处理器，漏 try/catch 就是白屏）
      fail({ type: 'toast', msg: TIP.PROD_CONFIRM_RETRY_FAIL });
      return null;
    }
    // 网络异常也会被服务层包成非成功信封，所以这里一定有 res
    if (res && isSuccessCode(res.code)) {
      clearTask();
      hide();
      if (t.onSuccess) t.onSuccess(res);
      return res;
    }
    const action = toUiAction(res);
    // 非 07020041：正常失败（状态不允许 / 无授权 / 重复请求…）→ 原样交调用方
    if (!isProdConfirmRequired(res)) {
      fail(action);
      return res;
    }
    // 07020041（一码两义）——先排除「明确不是生产」和「已经带过 token」这两种，避免弹无用的框
    if (t.prodbranch === false) {
      fail(action);
      return res;
    }
    if (token) {
      // 已经带 token 还被拒：token 过期/被用过，或根本不是「缺确认」而是环境不一致（G2）。
      // 🔴 不再自动重弹确认框 —— 否则用户会被一个永远不会成功的框反复套住，只把后端原话吐出来。
      fail({ type: 'toast', msg: res.msg || TIP.PROD_CONFIRM_RETRY_FAIL });
      return res;
    }
    if (t.prodbranch === true) {
      // 调用方已确定是生产（新建发布选 master）：直接弹框，确认时才去取 token
      setView((v) => ({ ...v, visible: true, note: '' }));
      return res;
    }
    // 不确定（详情页 4 个写操作）：拿 /confirm/issue 的 prodEnv 当判据
    const issued = await issue(t);
    if (!issued.ok) {
      fail(issued.action);
      return res;
    }
    if (!issued.prodEnv) {
      // 后端说这次不是生产 → 那 07020041 的另一半含义（G2 分支↔环境不一致）才是真原因
      fail(action);
      return res;
    }
    held.current = issued.confirmToken; // 顺手用掉刚签发的 token，用户确认后不再签一次
    setView((v) => ({ ...v, visible: true, note: '' }));
    return res;
  };

  /**
   * 起一次写流程（🔴 唯一入口）。
   * `confirmFirst=true` 时先弹确认框，用户点确认才发第一个请求（新建发布选 master 的路径）。
   */
  const run = async ({
    purpose,
    targetId = null,
    requestId,
    title = '',
    message = '',
    prodbranch,
    call,
    confirmFirst = false,
    onSuccess,
    onError,
  }) => {
    task.current = {
      purpose,
      targetId,
      requestId,
      prodbranch,
      call,
      onSuccess,
      onError,
    };
    held.current = null;
    setView((v) => ({ ...v, title: title || '', message, note: '' }));
    if (confirmFirst) {
      setView((v) => ({ ...v, visible: true }));
      return undefined;
    }
    return attempt(null);
  };

  /** 确认框点【确认发布生产】：取 token（或用手上已签发的）→ 同一 requestId 重发 */
  const confirm = async () => {
    const t = task.current;
    if (!t) return undefined;
    if (held.current) {
      const token = held.current;
      held.current = null;
      return attempt(token);
    }
    setView((v) => ({ ...v, loading: true }));
    const issued = await issue(t);
    if (!issued.ok) {
      // 取 token 失败：确认框**不关**（请求一个字节都没发出去），把原因标在框里让用户原地点第二次
      setView((v) => ({
        ...v,
        loading: false,
        note: (issued.action && issued.action.msg) || '',
      }));
      return undefined;
    }
    setView((v) => ({ ...v, loading: false, note: '' }));
    return attempt(issued.confirmToken);
  };

  /** 取消：只关框、丢上下文，不发任何请求（用户要再操作得重新点按钮 → 新 requestId） */
  const cancel = () => {
    clearTask();
    hide();
  };

  return {
    visible: view.visible,
    loading: view.loading,
    title: view.title,
    message: view.message,
    note: view.note,
    run,
    confirm,
    cancel,
  };
}
